import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { getApiUrl } from './config';

function FirmSettings() {
    const [firm, setFirm] = useState({
        name: '',
        address: '',
        contact: '',
        email: '',
        gst_no: '',
        sale_share_whatsapp_text: 1,
        sale_share_pdf: 0,
        sale_share_pdf_days: 10
    });
    const isAdmin = String(localStorage.getItem("role") || "") === "admin";
    const [backupBusy, setBackupBusy] = useState(false);
    const [restoreBusy, setRestoreBusy] = useState(false);
    const [restoreFileName, setRestoreFileName] = useState("");
    const [restoreData, setRestoreData] = useState(null);

    useEffect(() => {
        const fetchFirm = async () => {
            try {
                const res = await axios.get(`${getApiUrl()}/firm-settings`);
                if (res.data && typeof res.data === "object") {
                    setFirm(prev => ({
                        ...prev,
                        ...res.data,
                        sale_share_whatsapp_text: Number(res.data.sale_share_whatsapp_text) ? 1 : 0,
                        sale_share_pdf: Number(res.data.sale_share_pdf) ? 1 : 0,
                        sale_share_pdf_days: Number(res.data.sale_share_pdf_days) || 10
                    }));
                }
            } catch (err) {
                console.error(err);
            }
        };
        fetchFirm();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.post(`${getApiUrl()}/firm-settings`, firm);
            alert('Settings Saved');
        } catch (err) {
            console.error(err);
        }
    };

    const downloadBackup = async () => {
        if (!isAdmin) return alert("Admin only");
        try {
            setBackupBusy(true);
            const res = await axios.get(`${getApiUrl()}/admin/db-backup`, {
                responseType: "blob",
                params: { format: "sql" }
            });
            const disposition = res.headers && (res.headers["content-disposition"] || res.headers["Content-Disposition"]);
            const match = String(disposition || "").match(/filename="?([^"]+)"?/i);
            const filename = match ? match[1] : "backup.sql";
            const blobUrl = window.URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(blobUrl);
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to download backup";
            alert(message);
        } finally {
            setBackupBusy(false);
        }
    };

    const handleRestoreFile = (e) => {
        const file = e.target.files && e.target.files[0];
        if (!file) {
            setRestoreFileName("");
            setRestoreData(null);
            return;
        }
        setRestoreFileName(file.name || "");
        const reader = new FileReader();
        reader.onload = () => {
            try {
                const text = String(reader.result || "");
                const parsed = JSON.parse(text);
                setRestoreData(parsed);
            } catch (err) {
                console.error(err);
                setRestoreData(null);
                alert("Invalid backup file");
            }
        };
        reader.readAsText(file);
    };

    const restoreBackup = async () => {
        if (!isAdmin) return alert("Admin only");
        if (!restoreData) return alert("Select a valid backup file");
        const ok = window.confirm("Restore database? Current data will be replaced.");
        if (!ok) return;
        try {
            setRestoreBusy(true);
            await axios.post(`${getApiUrl()}/admin/db-restore`, restoreData);
            alert("Database restored");
        } catch (err) {
            console.error(err);
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.sqlMessage ||
                err?.response?.data?.code ||
                "Failed to restore backup";
            alert(message);
        } finally {
            setRestoreBusy(false);
        }
    };

    return (
        <Layout>
            <div className="container mt-4">
                <h2>Firm Settings</h2>
                <form onSubmit={handleSubmit} className="card p-4">
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Firm Name</label>
                            <input type="text" className="form-control" value={firm.name} onChange={e => setFirm({...firm, name: e.target.value})} onFocus={(e) => e.target.select()} required />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">GST No</label>
                            <input type="text" className="form-control" value={firm.gst_no} onChange={e => setFirm({...firm, gst_no: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Address</label>
                            <textarea className="form-control" value={firm.address} onChange={e => setFirm({...firm, address: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Contact</label>
                            <input type="text" className="form-control" value={firm.contact} onChange={e => setFirm({...firm, contact: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Email</label>
                            <input type="email" className="form-control" value={firm.email} onChange={e => setFirm({...firm, email: e.target.value})} onFocus={(e) => e.target.select()} />
                        </div>
                        <div className="col-12">
                            <hr className="my-2" />
                            <h5 className="mb-2">Sale Share Settings</h5>
                        </div>
                        <div className="col-md-6">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sale_share_whatsapp_text"
                                    checked={Number(firm.sale_share_whatsapp_text) ? true : false}
                                    onChange={(e) => setFirm({ ...firm, sale_share_whatsapp_text: e.target.checked ? 1 : 0 })}
                                />
                                <label className="form-check-label" htmlFor="sale_share_whatsapp_text">
                                    WhatsApp Text after Save/Update
                                </label>
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="form-check">
                                <input
                                    className="form-check-input"
                                    type="checkbox"
                                    id="sale_share_pdf"
                                    checked={Number(firm.sale_share_pdf) ? true : false}
                                    onChange={(e) => setFirm({ ...firm, sale_share_pdf: e.target.checked ? 1 : 0 })}
                                />
                                <label className="form-check-label" htmlFor="sale_share_pdf">
                                    PDF Share after Save/Update (Sales Report)
                                </label>
                            </div>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">PDF Days</label>
                            <input
                                type="number"
                                className="form-control"
                                value={firm.sale_share_pdf_days}
                                min={1}
                                max={60}
                                onChange={(e) => setFirm({ ...firm, sale_share_pdf_days: Number(e.target.value) || 10 })}
                                onFocus={(e) => e.target.select()}
                            />
                            <div className="form-text">Previous N days report will be shared as PDF.</div>
                        </div>
                        <div className="col-12 mt-3 d-grid d-md-block">
                            <button type="submit" className="btn btn-primary btn-lg">Save Settings</button>
                        </div>
                    </div>
                </form>

                {isAdmin && (
                    <div className="card p-4 mt-4">
                        <h5 className="mb-3">Database Backup</h5>
                        <div className="d-flex flex-wrap gap-2">
                            <button type="button" className="btn btn-outline-primary" onClick={downloadBackup} disabled={backupBusy || restoreBusy}>
                                {backupBusy ? "Downloading..." : "Download Backup"}
                            </button>
                        </div>

                        <hr className="my-4" />

                        <h5 className="mb-3">Database Restore</h5>
                        <div className="row g-3 align-items-end">
                            <div className="col-md-8">
                                <label className="form-label">Backup File (.json)</label>
                                <input className="form-control" type="file" accept="application/json,.json" onChange={handleRestoreFile} disabled={backupBusy || restoreBusy} />
                                {restoreFileName ? <div className="form-text">Selected: {restoreFileName}</div> : null}
                            </div>
                            <div className="col-md-4 d-grid">
                                <button type="button" className="btn btn-danger" onClick={restoreBackup} disabled={backupBusy || restoreBusy || !restoreData}>
                                    {restoreBusy ? "Restoring..." : "Restore Backup"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}

export default FirmSettings;
